// Sign-in modal: Google one-click + email/password (sign in, sign up, forgot
// password). After ANY Firebase sign-in we POST the resulting ID token to
// /firebase_signin which verifies the token, upserts userdata by email, and
// returns the MongoDB `verificationid` the rest of the app keys off of.
import * as React from 'react'
import {useEffect, useState} from 'react'
// @ts-ignore
import {FcGoogle} from 'react-icons/fc'
// @ts-ignore
import {toast} from 'react-toastify'
// @ts-ignore
import axios from 'axios'
// @ts-ignore
import {
	signInWithPopup,
	signInWithEmailAndPassword,
	createUserWithEmailAndPassword,
	sendPasswordResetEmail,
} from 'firebase/auth'
// @ts-ignore
import {auth, googleProvider} from '../../firebase'
// @ts-ignore
import {getAxiosBaseUrl} from '../../config.js'

// Map Firebase auth/* error codes to copy a real user can act on. Anything
// not in this table falls through to a generic message at the call site.
function describeFirebaseAuthError(err) {
	switch (err && err.code) {
		case 'auth/invalid-email':         return 'That email looks invalid.'
		case 'auth/user-not-found':        return 'No account with that email.'
		case 'auth/wrong-password':
		case 'auth/invalid-credential':    return 'Email or password is wrong.'
		case 'auth/email-already-in-use':  return 'An account already exists for this email — try signing in instead.'
		case 'auth/weak-password':         return 'Password must be at least 6 characters.'
		case 'auth/too-many-requests':     return 'Too many attempts. Try again in a few minutes.'
		case 'auth/operation-not-allowed': return 'Email sign-in is not enabled for this project yet.'
		case 'auth/popup-closed-by-user':  return 'Sign-in cancelled.'
		default: return null
	}
}

export const SigninForm = ({onClose, initialMode}) => {
	const [busy, setBusy] = useState(false)
	const [mode, setMode] = useState(initialMode === 'signup' ? 'signup' : 'signin')
	const [email, setEmail] = useState('')
	const [password, setPassword] = useState('')
	// Inline banner mirrors the toast so errors aren't easy to miss — top-right
	// toasts get lost when the user is focused on the modal.
	const [status, setStatus] = useState(null) // {kind: 'error'|'info'|'success', text: string}

	const handleEsc = (event) => {
		if (event.keyCode === 27 && typeof onClose === 'function') {
			document.removeEventListener('keydown', handleEsc)
			onClose()
		}
	}

	useEffect(() => {
		document.addEventListener('keydown', handleEsc)
		return () => document.removeEventListener('keydown', handleEsc)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Common post-sign-in step used by both Google and email/password paths.
	// Returns true on success (page reloads) so callers can decide whether
	// to clear the busy state.
	function showError(text) {
		setStatus({kind: 'error', text})
		toast.error(text, {position: 'top-right', autoClose: 2800})
	}
	function showInfo(text) {
		setStatus({kind: 'info', text})
		toast.info(text, {position: 'top-right', autoClose: 2800})
	}
	function showSuccess(text) {
		setStatus({kind: 'success', text})
		toast.success(text, {position: 'top-right', autoClose: 2200})
	}

	async function postSigninToBackend(user) {
		const idToken = await user.getIdToken()
		const resp = await axios.post(getAxiosBaseUrl() + '/firebase_signin', {
			firebaseUserId: user.uid,
			email: user.email,
			idToken,
		})
		if (resp && resp.data && resp.data.success && resp.data.verificationid) {
			sessionStorage.setItem('signedin', 'true')
			sessionStorage.setItem('verifiedemail', resp.data.email || user.email)
			sessionStorage.setItem('verificationid', resp.data.verificationid)
			showSuccess(mode === 'signup' ? 'Account created — sending verification email…' : 'Signed in')
			// Brief pause so the user sees the inline confirmation before reload.
			await new Promise(r => setTimeout(r, 700))
			window.location.reload()
			return true
		}
		showError((resp && resp.data && resp.data.message) || 'Sign-in failed')
		return false
	}

	async function onGoogleSignIn() {
		if (busy) return
		setStatus(null)
		setBusy(true)
		try {
			const result = await signInWithPopup(auth, googleProvider)
			const ok = await postSigninToBackend(result.user)
			if (!ok) setBusy(false)
		} catch (err) {
			console.log('Google sign-in error', err)
			showError(describeFirebaseAuthError(err) || 'Sign-in failed')
			setBusy(false)
		}
	}

	async function onEmailSubmit(e) {
		e.preventDefault()
		if (busy) return
		setStatus(null)
		const trimmedEmail = email.trim()
		if (!trimmedEmail || !password) {
			showError('Email and password are required')
			return
		}
		setBusy(true)
		try {
			const result = mode === 'signup'
				? await createUserWithEmailAndPassword(auth, trimmedEmail, password)
				: await signInWithEmailAndPassword(auth, trimmedEmail, password)
			const ok = await postSigninToBackend(result.user)
			if (!ok) setBusy(false)
		} catch (err) {
			console.log(`${mode} error`, err)
			showError(describeFirebaseAuthError(err) || 'Sign-in failed')
			setBusy(false)
		}
	}

	async function onForgotPassword() {
		const trimmedEmail = email.trim()
		if (!trimmedEmail) {
			showInfo('Enter your email above first, then click Forgot password.')
			return
		}
		try {
			await sendPasswordResetEmail(auth, trimmedEmail)
			showSuccess('Password reset email sent')
		} catch (err) {
			console.log('reset error', err)
			showError(describeFirebaseAuthError(err) || "Couldn't send reset email")
		}
	}

	const submitLabel = busy ? 'Working…' : (mode === 'signup' ? 'Create account' : 'Sign in')

	const bannerClass = status && (
		status.kind === 'error' ? 'bg-red-50 border-red-200 text-red-700'
		: status.kind === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
		: 'bg-slate-50 border-slate-200 text-slate-700'
	)

	return (
		<div className='flex flex-col gap-4 py-2 w-full max-w-sm mx-auto'>
			{status && (
				<div role='alert' className={`text-sm rounded-xl border px-3 py-2 ${bannerClass}`}>
					{status.text}
				</div>
			)}
			<button
				type='button'
				onClick={onGoogleSignIn}
				disabled={busy}
				className='flex items-center justify-center gap-3 bg-white border border-slate-200 rounded-full px-6 py-3 hover:bg-slate-50 transition disabled:opacity-60 disabled:cursor-not-allowed text-slate-800 font-medium shadow-sm'
			>
				<FcGoogle size={22}/>
				<span>Continue with Google</span>
			</button>

			<div className='flex items-center gap-3 text-xs text-slate-400'>
				<div className='flex-1 h-px bg-slate-200'/>
				or
				<div className='flex-1 h-px bg-slate-200'/>
			</div>

			<form onSubmit={onEmailSubmit} className='flex flex-col gap-3'>
				<input
					type='email'
					autoComplete='email'
					required
					value={email}
					onChange={(e) => setEmail(e.target.value)}
					placeholder='you@example.com'
					className='border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none'
				/>
				<input
					type='password'
					autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
					required
					minLength={6}
					value={password}
					onChange={(e) => setPassword(e.target.value)}
					placeholder={mode === 'signup' ? 'Password (min 6 characters)' : 'Password'}
					className='border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none'
				/>
				<button
					type='submit'
					disabled={busy}
					className='bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-full px-6 py-2.5 font-semibold shadow-sm transition'>
					{submitLabel}
				</button>
			</form>

			<div className='flex items-center justify-between text-xs text-slate-500'>
				<button
					type='button'
					onClick={() => setMode(mode === 'signup' ? 'signin' : 'signup')}
					className='hover:text-emerald-600'>
					{mode === 'signup' ? 'Have an account? Sign in' : 'New here? Create account'}
				</button>
				{mode !== 'signup' && (
					<button
						type='button'
						onClick={onForgotPassword}
						className='hover:text-emerald-600'>
						Forgot password?
					</button>
				)}
			</div>
		</div>
	)
}

export default SigninForm
